import { useEffect, useReducer } from "react";
import "../../../../app/App.scss";
import { IntlProvider } from "react-intl";
import ENGLISH from "../../../../i18n/languages/en-US.json";
import { pig } from "../../../../static/js/pig.js";
import Header from "../export/Header";
import OrganizationChart from "../export/OrganizationChart";
import { sendRequest } from "@/common/http";
import MissionAndOutcomesTable from "../export/MissionAndOutcomesTable";
import translate from "@/i18n/translate";
import { Lead } from "@/features/common/interfaces/Interface";
import Loader from "../../../common/elements/Loader";
import ProgramChart from "../export/ProgramsChart";
import ProgramsTable from "../export/ProgramsTable";
import CoursesChart from "../export/CoursesChart";
import CoursesTable from "../export/CoursesTable";
import UnauthorizedComponent from "@/features/insights/UnauthorizedComponent";
import {
  ORGANIZATIONS_API_ERROR,
  PROGRAMS_API_ERROR,
  COURSES_API_ERROR
} from "@/features/common/constants";
import { OutcomesTerminology } from "@/contexts/AppContext";

export interface OrganizationProgressData {
  totalOrgs: number | null;
  organizationMissionIncomplete: number;
  organizationMissionComplete: number;
  learningOutcomes: number;
  learningOutcomesIncomplete: number;
  otherOutcomes: number;
  otherOutcomesIncomplete: number;
  iloMappings: number;
  iloMappingsIncomplete: number;
}
export interface CourseProgressData {
  courseIloComplete: number;
  courseIloNotStarted: number;
  courseLearningOutcomesComplete: number;
  courseLearningOutcomesNotStarted: number;
  totalCourses: number | null;
}
export interface ProgramProgressState {
  totalPrograms: number | null;
  programMissionComplete: number;
  programMissionIncomplete: number;
  programLearningOutcomes: number;
  programLearningOutcomesIncomplete: number;
  programSuccessOutcomes: number;
  programSuccessOutcomesIncomplete: number;
  programCurriculumMapsComplete: number;
  programCurriculumMapsIncomplete: number;
  iloProgramMappingsComplete: number;
  iloProgramMappingsIncomplete: number;
}

export interface MissionAndOutcomesHtmlState {
  locale: string;
  messages: any;
  totalOrgs: number | null;
  organizationMissionIncomplete: number;
  organizationMissionComplete: number;
  learningOutcomes: number;
  learningOutcomesIncomplete: number;
  otherOutcomes: number;
  otherOutcomesIncomplete: number;
  iloMappings: number;
  iloMappingsIncomplete: number;
  organizationApiError: boolean;
  missionHierarchy: Hierarchy;
  successHierarchy: Hierarchy;
  learningHierarchy: Hierarchy;
  iloHierarchy: Hierarchy;
  programMissionHierarchy: ProgramHierarchy[] | null;
  programSuccessHierarchy: ProgramHierarchy[] | null;
  programLearningHierarchy: ProgramHierarchy[] | null;
  programIloHierarchy: ProgramHierarchy[] | null;
  programCurriculumHierarchy: ProgramHierarchy[] | null;
  leads: Lead[];
  totalPrograms: number | null;
  programMissionComplete: number;
  programMissionIncomplete: number;
  programLearningOutcomes: number;
  programLearningOutcomesIncomplete: number;
  programSuccessOutcomes: number;
  programSuccessOutcomesIncomplete: number;
  programCurriculumMapsComplete: number;
  programCurriculumMapsIncomplete: number;
  iloProgramMappingsComplete: number;
  iloProgramMappingsIncomplete: number;
  programApiError: boolean;
  courseData: CourseProgressData;
  courseApiError: boolean;
  outcomesTerminology: OutcomesTerminology | null;
}
interface MissionAndOutcomesHtmlAction {
  type: string;
  locale: string | null;
  orgData: OrganizationProgressData;
  organizationApiError: boolean;
  programData: ProgramProgressState;
  missionHierarchy: Hierarchy;
  successHierarchy: Hierarchy;
  learningHierarchy: Hierarchy;
  iloHierarchy: Hierarchy;
  programMissionHierarchy: ProgramHierarchy[] | null;
  programSuccessHierarchy: ProgramHierarchy[] | null;
  programLearningHierarchy: ProgramHierarchy[] | null;
  programIloHierarchy: ProgramHierarchy[] | null;
  programCurriculumHierarchy: ProgramHierarchy[] | null;
  programApiError: boolean;
  leads: Lead[];
  courseData: CourseProgressData;
  courseApiError: boolean;
  errorType: string;
  outcomesTerminology: OutcomesTerminology | null;
}
const defaultHierarchy = {
  level: 0,
  name: "",
  show_stats: false,
  status: "",
  type: "",
  uuid: "",
  children: [],
  outcomes: 0,
  ilo_mapped: 0
};

const missionAndOutcomesHtmlAction = {
  type: "",
  locale: "en-US",
  orgData: {
    totalOrgs: null,
    organizationMissionIncomplete: 0,
    organizationMissionComplete: 0,
    learningOutcomes: 0,
    learningOutcomesIncomplete: 0,
    otherOutcomes: 0,
    otherOutcomesIncomplete: 0,
    iloMappings: 0,
    iloMappingsIncomplete: 0
  },
  organizationApiError: false,
  programData: {
    totalPrograms: null,
    programMissionComplete: 0,
    programMissionIncomplete: 0,
    programLearningOutcomes: 0,
    programLearningOutcomesIncomplete: 0,
    programSuccessOutcomes: 0,
    programSuccessOutcomesIncomplete: 0,
    programCurriculumMapsComplete: 0,
    programCurriculumMapsIncomplete: 0,
    iloProgramMappingsComplete: 0,
    iloProgramMappingsIncomplete: 0
  },
  missionHierarchy: {
    level: 0,
    name: "",
    show_stats: false,
    status: "",
    type: "",
    uuid: "",
    updated_at: "",
    children: []
  },
  successHierarchy: defaultHierarchy,
  learningHierarchy: defaultHierarchy,
  iloHierarchy: defaultHierarchy,
  programMissionHierarchy: null,
  programSuccessHierarchy: null,
  programLearningHierarchy: null,
  programIloHierarchy: null,
  programCurriculumHierarchy: null,
  programApiError: false,
  leads: [],
  courseData: {
    courseIloComplete: 0,
    courseIloNotStarted: 0,
    courseLearningOutcomesComplete: 0,
    courseLearningOutcomesNotStarted: 0,
    totalCourses: null
  },
  courseApiError: false,
  errorType: "",
  outcomesTerminology: null
};

export interface Hierarchy {
  level: number;
  name: string;
  show_stats: boolean;
  status: string;
  type: string;
  uuid: string;
  updated_at?: string;
  children: Hierarchy[];
  outcomes?: number;
  ilo_mapped?: number;
}
export interface ProgramHierarchy {
  level: number;
  name: string;
  show_stats: boolean;
  uuid: string;
  children: ProgramHierarchy[];
  type?: string;
  status?: string;
  updated_at?: string;
  ilo_mapped?: string;
  outcomes?: number;
}
const initialState = {
  locale: "en-US",
  messages: null,
  totalOrgs: null,
  organizationMissionIncomplete: 0,
  organizationMissionComplete: 0,
  learningOutcomes: 0,
  learningOutcomesIncomplete: 0,
  otherOutcomes: 0,
  otherOutcomesIncomplete: 0,
  iloMappings: 0,
  iloMappingsIncomplete: 0,
  organizationApiError: false,
  totalPrograms: null,
  programMissionComplete: 0,
  programMissionIncomplete: 0,
  programLearningOutcomes: 0,
  programLearningOutcomesIncomplete: 0,
  programSuccessOutcomes: 0,
  programSuccessOutcomesIncomplete: 0,
  programCurriculumMapsComplete: 0,
  programCurriculumMapsIncomplete: 0,
  iloProgramMappingsComplete: 0,
  iloProgramMappingsIncomplete: 0,
  missionHierarchy: {
    level: 0,
    name: "",
    show_stats: false,
    status: "",
    type: "",
    uuid: "",
    updated_at: "",
    children: []
  },
  successHierarchy: defaultHierarchy,
  learningHierarchy: defaultHierarchy,
  iloHierarchy: defaultHierarchy,
  programMissionHierarchy: null,
  programSuccessHierarchy: null,
  programLearningHierarchy: null,
  programIloHierarchy: null,
  programCurriculumHierarchy: [],
  programApiError: false,
  leads: [],
  courseData: {
    courseIloComplete: 0,
    courseIloNotStarted: 0,
    courseLearningOutcomesComplete: 0,
    courseLearningOutcomesNotStarted: 0,
    totalCourses: null
  },
  courseApiError: false,
  outcomesTerminology: null
};

export interface MissionAndOutcomesExportViewProps {}

export default function MissionAndOutcomesExportView(): JSX.Element {
  const reducer = (
    state: MissionAndOutcomesHtmlState,
    action: MissionAndOutcomesHtmlAction
  ) => {
    switch (action.type) {
      case "setLocale":
        return setLocale(state, action.locale);
      case "setOrgData":
        return {
          ...state,
          totalOrgs: action.orgData.totalOrgs,
          organizationMissionIncomplete:
            action.orgData.organizationMissionIncomplete,
          organizationMissionComplete:
            action.orgData.organizationMissionComplete,
          learningOutcomes: action.orgData.learningOutcomes,
          learningOutcomesIncomplete: action.orgData.learningOutcomesIncomplete,
          otherOutcomes: action.orgData.otherOutcomes,
          otherOutcomesIncomplete: action.orgData.otherOutcomesIncomplete,
          iloMappings: action.orgData.iloMappings,
          iloMappingsIncomplete: action.orgData.iloMappingsIncomplete
        };
      case "setHierarchyMission":
        return {
          ...state,
          missionHierarchy: action.missionHierarchy
        };
      case "setHierarchyLearning":
        return {
          ...state,
          learningHierarchy: action.learningHierarchy
        };
      case "setHierarchySuccess":
        return {
          ...state,
          successHierarchy: action.successHierarchy
        };
      case "setHierarchyIlo":
        return {
          ...state,
          iloHierarchy: action.iloHierarchy
        };
      case "setLeads":
        return {
          ...state,
          leads: action.leads
        };
      case "setProgramData":
        return {
          ...state,
          totalPrograms: action.programData.totalPrograms,
          programMissionComplete: action.programData.programMissionComplete,
          programMissionIncomplete: action.programData.programMissionIncomplete,
          programLearningOutcomes: action.programData.programLearningOutcomes,
          programLearningOutcomesIncomplete:
            action.programData.programLearningOutcomesIncomplete,
          programSuccessOutcomes: action.programData.programSuccessOutcomes,
          programSuccessOutcomesIncomplete:
            action.programData.programSuccessOutcomesIncomplete,
          programCurriculumMapsComplete:
            action.programData.programCurriculumMapsComplete,
          programCurriculumMapsIncomplete:
            action.programData.programCurriculumMapsIncomplete,
          iloProgramMappingsComplete:
            action.programData.iloProgramMappingsComplete,
          iloProgramMappingsIncomplete:
            action.programData.iloProgramMappingsIncomplete
        };
      case "setCourseData":
        return {
          ...state,
          courseData: action.courseData
        };
      case "setHierarchyProgramMission":
        return {
          ...state,
          programMissionHierarchy: action.programMissionHierarchy
        };
      case "setHierarchyProgramLearning":
        return {
          ...state,
          programLearningHierarchy: action.programLearningHierarchy
        };
      case "setHierarchyProgramSuccess":
        return {
          ...state,
          programSuccessHierarchy: action.programSuccessHierarchy
        };
      case "setHierarchyProgramIlo":
        return {
          ...state,
          programIloHierarchy: action.programIloHierarchy
        };
      case "setHierarchyProgramCurriculum":
        return {
          ...state,
          programCurriculumHierarchy: action.programCurriculumHierarchy
        };
      case "setApiError":
        return setApiError(state, action.errorType);
      case "setOutcomesTerminology":
        return {
          ...state,
          outcomesTerminology: action.outcomesTerminology
        };
      default:
        return state;
    }
  };
  const setLocale = (
    state: MissionAndOutcomesHtmlState,
    locale: string | null
  ) => {
    switch (locale) {
      case "pig": {
        return { ...state, locale: "en-US", messages: pig(ENGLISH) };
      }
      default: {
        return { ...state, locale: "en-US", messages: ENGLISH };
      }
    }
  };
  const setApiError = (
    state: MissionAndOutcomesHtmlState,
    errorType: string
  ) => {
    switch (errorType) {
      case ORGANIZATIONS_API_ERROR: {
        return { ...state, organizationApiError: true };
      }
      case PROGRAMS_API_ERROR: {
        return { ...state, programApiError: true };
      }
      case COURSES_API_ERROR: {
        return { ...state, courseApiError: true };
      }
      default: {
        return { ...state };
      }
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale");
    dispatch({
      ...missionAndOutcomesHtmlAction,
      type: "setLocale",
      locale: locale
    });
  }, []);

  useEffect(() => {
    const applyResult = (result: OutcomesTerminology) => {
      if (result) {
        dispatch({
          ...missionAndOutcomesHtmlAction,
          type: "setOutcomesTerminology",
          outcomesTerminology: result
        });
      }
    };
    sendRequest(
        { uri: "/outcomes-terminology", camelize: true },
        applyResult
      );
  }, []);

  useEffect(() => {
    const applyLeadsResult = (result: Lead[]) => {
      dispatch({
        ...missionAndOutcomesHtmlAction,
        type: "setLeads",
        leads: result
      });
    };
    sendRequest({ uri: "/leads", camelize: true }, applyLeadsResult);
  }, []);

  useEffect(() => {
    const applyResult = (result: OrganizationProgressData) => {
      dispatch({
        ...missionAndOutcomesHtmlAction,
        type: "setOrgData",
        orgData: result
      });
    };
    const onError = () => {
      dispatch({
        ...missionAndOutcomesHtmlAction,
        type: "setApiError",
        errorType: ORGANIZATIONS_API_ERROR
      });
    };
    sendRequest(
        { uri: "/mission_outcome/organizations", camelize: true },
        applyResult,
        onError
      );
  }, []);

  useEffect(() => {
    const applyResult = (result: ProgramProgressState) => {
      dispatch({
        ...missionAndOutcomesHtmlAction,
        type: "setProgramData",
        programData: result
      });
    };
    const onError = () => {
      dispatch({
        ...missionAndOutcomesHtmlAction,
        type: "setApiError",
        errorType: PROGRAMS_API_ERROR
      });
    };
    sendRequest(
        { uri: "/mission_outcome/programs", camelize: true },
        applyResult,
        onError
      );
  }, []);

  useEffect(() => {
    const applyResult = (result: CourseProgressData) => {
      dispatch({
        ...missionAndOutcomesHtmlAction,
        type: "setCourseData",
        courseData: result
      });
    };
    const onError = () => {
      dispatch({
        ...missionAndOutcomesHtmlAction,
        type: "setApiError",
        errorType: COURSES_API_ERROR
      });
    };
    sendRequest(
        { uri: "/mission_outcome/courses", camelize: true },
        applyResult,
        onError
      );
  }, []);

  function getHierarchy(segment: string) {
    const applyResult = (result: Hierarchy) => {
      switch (segment) {
        case "organization_mission": {
          return dispatch({
            ...missionAndOutcomesHtmlAction,
            type: "setHierarchyMission",
            missionHierarchy: result
          });
        }
        case "organization_learning_outcome": {
          return dispatch({
            ...missionAndOutcomesHtmlAction,
            type: "setHierarchyLearning",
            learningHierarchy: result
          });
        }
        case "organization_success_outcome": {
          return dispatch({
            ...missionAndOutcomesHtmlAction,
            type: "setHierarchySuccess",
            successHierarchy: result
          });
        }
        case "organization_ilo_mapping": {
          return dispatch({
            ...missionAndOutcomesHtmlAction,
            type: "setHierarchyIlo",
            iloHierarchy: result
          });
        }
      }
    };

    sendRequest({ uri: `/hierarchy?segment=${segment}` }, applyResult);
  }
  function getHierarchyProgram(segment: string) {
    const applyResult = (result: ProgramHierarchy[]) => {
      switch (segment) {
        case "program_mission": {
          return dispatch({
            ...missionAndOutcomesHtmlAction,
            type: "setHierarchyProgramMission",
            programMissionHierarchy: result
          });
        }
        case "program_learning_outcomes": {
          return dispatch({
            ...missionAndOutcomesHtmlAction,
            type: "setHierarchyProgramLearning",
            programLearningHierarchy: result
          });
        }
        case "program_success_outcomes": {
          return dispatch({
            ...missionAndOutcomesHtmlAction,
            type: "setHierarchyProgramSuccess",
            programSuccessHierarchy: result
          });
        }
        case "program_ilo_mapping": {
          return dispatch({
            ...missionAndOutcomesHtmlAction,
            type: "setHierarchyProgramIlo",
            programIloHierarchy: result
          });
        }
        case "program_cmap": {
          return dispatch({
            ...missionAndOutcomesHtmlAction,
            type: "setHierarchyProgramCurriculum",
            programCurriculumHierarchy: result
          });
        }
      }
    };

    sendRequest({ uri: `/hierarchy?segment=${segment}` }, applyResult);
  }
  useEffect(() => {
    getHierarchy("organization_mission");
    getHierarchy("organization_learning_outcome");
    getHierarchy("organization_success_outcome");
    getHierarchy("organization_ilo_mapping");
  }, []);
  useEffect(() => {
    getHierarchyProgram("program_mission");
    getHierarchyProgram("program_learning_outcomes");
    getHierarchyProgram("program_success_outcomes");
    getHierarchyProgram("program_ilo_mapping");
    getHierarchyProgram("program_cmap");
  }, []);

  const showLoader = () => {
    return (
      !state.missionHierarchy.name ||
      !state.successHierarchy.name ||
      !state.learningHierarchy.name ||
      !state.iloHierarchy.name ||
      state.programMissionHierarchy === null ||
      state.programSuccessHierarchy == null ||
      state.programLearningHierarchy === null ||
      state.programIloHierarchy === null ||
      state.programCurriculumHierarchy === null ||
      (state.totalOrgs === null && !state.organizationApiError) ||
      (state.totalPrograms === null && !state.programApiError) ||
      (state.courseData.totalCourses === null && !state.courseApiError)
    );
  };

  return (
    <>
      <div className="pagewrapper faculty-html-container">
          <div className="insights-hub-body flex-row">
            <div className="nav-main">
              <div className="contentwrapper">
                <main id="maincontent" className="main-container">
                  {state.messages && (
                    <IntlProvider
                      locale={state.locale}
                      messages={state.messages}
                    >
                      <div className="faculty-html-container">
                        <Header outcomesTerminology={state.outcomesTerminology} />
                        {showLoader() ? <Loader /> : ""}
                        <section
                          className="-mainheader -withbreadcrumbs only-breadcrumbs body-padding"
                          id="page-header"
                          data-export-id="mission-outcome-export"
                        >
                          <>
                            <OrganizationChart 
                              organizationData={state}
                              outcomesTerminology={state.outcomesTerminology}
                            />
                            <div className="organization-border">
                              <h3 className="title organization-heading">
                                {translate(
                                  "app.strategy.exportHtmlSubHeaderTable",
                                  {
                                    type: "Organization"
                                  }
                                )}
                              </h3>
                              {state.missionHierarchy.name &&
                                state.successHierarchy.name &&
                                state.learningHierarchy.name &&
                                state.iloHierarchy.name && (
                                  <div className="table-border">
                                    <MissionAndOutcomesTable
                                      missionHierarchy={state.missionHierarchy}
                                      successHierarchy={state.successHierarchy}
                                      learningHierarchy={
                                        state.learningHierarchy
                                      }
                                      iloHierarchy={state.iloHierarchy}
                                      leads={state.leads}
                                      outcomesTerminology={state.outcomesTerminology}
                                    />
                                  </div>
                                )}
                            </div>
                          </>
                          <>
                            <div className={"program-border"}>
                              <ProgramChart 
                                programData={state}
                                outcomesTerminology={state.outcomesTerminology}
                              />
                            </div>
                            <div className="organization-border">
                              <h3 className="title organization-heading">
                                {translate(
                                  "app.strategy.exportHtmlSubHeaderTable",
                                  {
                                    type: "Program"
                                  }
                                )}
                              </h3>
                              {state.programMissionHierarchy?.length &&
                                state.programSuccessHierarchy?.length &&
                                state.programLearningHierarchy?.length &&
                                state.programIloHierarchy?.length &&
                                state.programCurriculumHierarchy?.length ? (
                                <div className="table-border">
                                  <ProgramsTable
                                    missionHierarchy={
                                      state.programMissionHierarchy
                                    }
                                    successHierarchy={
                                      state.programSuccessHierarchy
                                    }
                                    learningHierarchy={
                                      state.programLearningHierarchy
                                    }
                                    iloHierarchy={state.programIloHierarchy}
                                    curriculumMap={
                                      state.programCurriculumHierarchy
                                    }
                                    leads={state.leads}
                                    outcomesTerminology={state.outcomesTerminology}
                                  />
                                </div>
                              ) : (
                                ""
                              )}
                            </div>
                          </>
                          <>
                            <div className={"program-border"}>
                              <CoursesChart
                                courseData={state.courseData}
                                courseApiError={state.courseApiError}
                                outcomesTerminology={state.outcomesTerminology}
                              />
                            </div>
                            <div className="organization-border">
                              <h3 className="title organization-heading">
                                {translate(
                                  "app.strategy.exportHtmlSubHeaderTable",
                                  {
                                    type: "Course"
                                  }
                                )}
                              </h3>
                              <div className="table-border courses-table margin-bottom-20">
                                <CoursesTable outcomesTerminology={state.outcomesTerminology} />
                              </div>
                            </div>
                          </>
                        </section>
                      </div>
                    </IntlProvider>
                  )}
                </main>
              </div>
            </div>
          </div>
        </div>
    </>
  );
}
